'use client';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { GridCarView, GridClientPuzzle } from '../lib/types';
import { GRID_SIZE, carFitsCell } from '../lib/types';

// The 3×3 board: corner plate, column/row trait headers, nine slots.
// Filled slots are drag sources for re-park / discard and carry an × remove
// button until the game ends. The per-constraint ✓/✗ chips are the live
// feedback — suppressed in one-shot mode (`hideFeedback`) until Submit.

export interface CellHandlers {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onPointerCancel: (e: ReactPointerEvent) => void;
}

export function GridBoard({
  puzzle,
  placements,
  carById,
  armedCell,
  hoverCell,
  liftCell,
  hideFeedback,
  done,
  onCellTap,
  onRemove,
  cellDragProps,
}: {
  puzzle: GridClientPuzzle;
  placements: (string | null)[];
  carById: Map<string, GridCarView>;
  armedCell: number | null;
  hoverCell: number | null;
  liftCell: number | null;
  hideFeedback: boolean; // one-shot mid-play: no ✓/✗ chips, no ✗ styling
  done: boolean;
  onCellTap: (cell: number) => void;
  onRemove: (cell: number) => void;
  cellDragProps: (carId: string, cell: number) => CellHandlers;
}) {
  return (
    <div className="gp-grid">
      <div className="gp-corner">
        <span className="gp-plate">№ {String(puzzle.number).padStart(3, '0')}</span>
      </div>
      {puzzle.cols.map((col) => (
        <div key={col.id} className="gp-hdr">
          <span className="gp-s">col · {col.dim}</span>
          <span className="gp-t">{col.label}</span>
        </div>
      ))}
      {puzzle.rows.map((row, r) => (
        <RowCells
          key={row.id}
          rowIndex={r}
          rowLabel={row.label}
          rowDim={row.dim}
          puzzle={puzzle}
          placements={placements}
          carById={carById}
          armedCell={armedCell}
          hoverCell={hoverCell}
          liftCell={liftCell}
          hideFeedback={hideFeedback}
          done={done}
          onCellTap={onCellTap}
          onRemove={onRemove}
          cellDragProps={cellDragProps}
        />
      ))}
    </div>
  );
}

function RowCells({
  rowIndex,
  rowLabel,
  rowDim,
  puzzle,
  placements,
  carById,
  armedCell,
  hoverCell,
  liftCell,
  hideFeedback,
  done,
  onCellTap,
  onRemove,
  cellDragProps,
}: {
  rowIndex: number;
  rowLabel: string;
  rowDim: string;
  puzzle: GridClientPuzzle;
  placements: (string | null)[];
  carById: Map<string, GridCarView>;
  armedCell: number | null;
  hoverCell: number | null;
  liftCell: number | null;
  hideFeedback: boolean;
  done: boolean;
  onCellTap: (cell: number) => void;
  onRemove: (cell: number) => void;
  cellDragProps: (carId: string, cell: number) => CellHandlers;
}) {
  return (
    <>
      <div className="gp-hdr gp-row">
        <span className="gp-s">row · {rowDim}</span>
        <span className="gp-t">{rowLabel}</span>
      </div>
      {puzzle.cols.map((col, c) => {
        const cell = rowIndex * GRID_SIZE + c;
        const carId = placements[cell];
        const car = carId ? carById.get(carId) : undefined;
        if (!car) {
          return (
            <div
              key={col.id}
              data-gp-cell={cell}
              className={[
                'gp-cell',
                'empty',
                armedCell === cell ? 'armed' : '',
                hoverCell === cell ? 'dropglow' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onCellTap(cell)}
            >
              <span className="gp-ghost">+</span>
            </div>
          );
        }
        const ok = carFitsCell(car, cell);
        const rowOk = car.fits.rows[rowIndex];
        const colOk = car.fits.cols[c];
        const draggable = !done;
        return (
          <div
            key={col.id}
            data-gp-cell={cell}
            className={[
              'gp-cell',
              'filled',
              !hideFeedback && !ok ? 'bad' : '',
              liftCell === cell ? 'lift' : '',
              draggable ? '' : 'locked',
            ]
              .filter(Boolean)
              .join(' ')}
            {...(draggable ? cellDragProps(car.id, cell) : {})}
          >
            <div className="gp-placed">
              <div className="gp-nm">{car.label}</div>
              {!hideFeedback && (
                <div className="gp-why">
                  <span className={`gp-chip${rowOk ? '' : ' miss'}`}>
                    {puzzle.rows[rowIndex].id} {rowOk ? '✓' : '✗'}
                  </span>
                  <span className={`gp-chip${colOk ? '' : ' miss'}`}>
                    {col.id} {colOk ? '✓' : '✗'}
                  </span>
                </div>
              )}
            </div>
            {draggable && (
              <button
                type="button"
                className="gp-rm"
                aria-label={`Remove ${car.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(cell);
                }}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}
